// 主应用组件 - 配置路由和整体布局
import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import './i18n';
import Login from './pages/Login';
import Register from './pages/Register';
import ForgotPassword from './pages/ForgotPassword';
import APIKeys from './pages/APIKeys';
import Billing from './pages/Billing';
import Usage from './pages/Usage';
import Models from './pages/Models';
import Documentation from './pages/Documentation';
import Settings from './pages/Settings';
import Referrals from './pages/Referrals';
import TermsOfService from './pages/TermsOfService';
import PrivacyPolicy from './pages/PrivacyPolicy';
import ResetPassword from './pages/ResetPassword';

// 私有路由组件，检查用户是否已登录
const PrivateRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const apiKey = localStorage.getItem('apiKey');
  const currentPath = window.location.pathname;
  
  if (!apiKey) {
    // 如果是访问referrals页面，登录后重定向回referrals
    const redirectPath = currentPath === '/referrals' ? '/referrals' : '/dashboard/models';
    return <Navigate to={`/login?redirect=${encodeURIComponent(redirectPath)}`} />;
  }
  
  return <>{children}</>;
};

// 公共路由组件，如果已登录则重定向到模型页面
const PublicRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const apiKey = localStorage.getItem('apiKey');
  return !apiKey ? <>{children}</> : <Navigate to="/dashboard/models" />;
};

function App() {
  const { t } = useTranslation();
  
  return (
    <Router>
      <div className="App">
        <Routes>
          {/* 默认路由重定向到模型页面 */}
          <Route path="/" element={<Navigate to="/dashboard/models" />} />
          <Route path="/dashboard" element={<Navigate to="/dashboard/models" />} />

          {/* 公共路由 - 未登录用户可访问 */}
          <Route
            path="/login"
            element={
              <PublicRoute>
                <Login />
              </PublicRoute>
            }
          />
          <Route
            path="/register"
            element={
              <PublicRoute>
                <Register />
              </PublicRoute>
            }
          />
          <Route
            path="/register/:referralCode"
            element={
              <PublicRoute>
                <Register />
              </PublicRoute>
            }
          />

          <Route
            path="/forgot-password"
            element={
              <PublicRoute>
                <ForgotPassword />
              </PublicRoute>
            }
          />

          <Route
            path="/reset-password"
            element={
              <PublicRoute>
                <ResetPassword />
              </PublicRoute>
            }
          />

          <Route
            path="/terms"
            element={<TermsOfService />}
          />
          <Route
            path="/privacy"
            element={<PrivacyPolicy />}
          />

          {/* Independent Referrals page - requires login */}
          <Route
            path="/referrals"
            element={
              <PrivateRoute>
                <Referrals />
              </PrivateRoute>
            }
          />

          {/* 私有路由 - 需要登录才能访问 */}


          <Route
            path="/dashboard/keys"
            element={
              <PrivateRoute>
                <APIKeys />
              </PrivateRoute>
            }
          />

          <Route
            path="/dashboard/billing"
            element={
              <PrivateRoute>
                <Billing />
              </PrivateRoute>
            }
          />

          <Route
            path="/dashboard/usage"
            element={
              <PrivateRoute>
                <Usage />
              </PrivateRoute>
            }
          />
          <Route
            path="/dashboard/models"
            element={
              <PrivateRoute>
                <Models />
              </PrivateRoute>
            }
          />
          <Route
            path="/dashboard/documentation"
            element={
              <PrivateRoute>
                <Documentation />
              </PrivateRoute>
            }
          />
          <Route
            path="/dashboard/settings"
            element={
              <PrivateRoute>
                <Settings />
              </PrivateRoute>
            }
          />
          {/* Referrals moved to independent route */}

          {/* 其他未匹配路由 */}
          <Route
            path="/dashboard/*"
            element={
              <PrivateRoute>
                <div className="min-h-screen bg-gray-50 flex items-center justify-center">
                  <div className="text-center">
                    <h2 className="text-2xl font-bold text-gray-900 mb-4">{t('common.pageComingSoon')}</h2>
                    <p className="text-gray-600">{t('common.pageUnderDevelopment')}</p>
                  </div>
                </div>
              </PrivateRoute>
            }
          />

          {/* 404页面 */}
          <Route
            path="*"
            element={
              <div className="min-h-screen bg-gray-50 flex items-center justify-center">
                <div className="text-center">
                  <h2 className="text-2xl font-bold text-gray-900 mb-4">{t('common.pageNotFound')}</h2>
                  <p className="text-gray-600">{t('common.pageNotFoundDescription')}</p>
                </div>
              </div>
            }
          />
        </Routes>
      </div>
    </Router>
  );
}

export default App;